import type { WindowUser } from '@/types/user'
import type { PermissionType } from '@/types/general'

import * as Sentry from '@sentry/vue'
import axios, { AxiosResponse } from 'axios'
import { defineStore } from 'pinia'

import { LicenseType, type UserCompany } from '@/types/company'
import { DeliveryManagementPreferences } from '@/types/delivery-management'
import { moduleLicenses, ModuleLicenseType, readonlyLicenses } from '@/utils/licenses'
import { PackagingPreferences } from '@/types/packaging'

const currentCompanyKey = 'mypallet.currentCompanyId'

export type licenseObject = Partial<Record<LicenseType, Record<string, unknown>>>

interface AuthStoreState {
    user: WindowUser | null
    isAdmin: boolean
    isImpersonatingFilament: boolean
    isImpersonatingNova: boolean
    companyId: string
    companies: UserCompany[]
    licenses: licenseObject
}

let licensePromise: Promise<AxiosResponse<licenseObject>> | null = null

export const useAuthStore = defineStore('AuthStore', {
    state(): AuthStoreState {
        let companyId = localStorage.getItem(currentCompanyKey) || window.defaultCompanyId
        const hasCompany = !!(window.companies || []).find(({ id }) => id === companyId)
        if (!hasCompany) {
            companyId = window.defaultCompanyId
        }

        Sentry.setContext('currentCompany', { id: companyId })

        return {
            user: window.user,
            isAdmin: window.isAdmin,
            isImpersonatingFilament: window.isImpersonating,
            isImpersonatingNova: window.isImpersonatingNova,
            companies: window.companies ?? [],
            licenses: {},
            companyId,
        }
    },
    getters: {
        /**
         * Determines if the company is globally readonly.
         * This is used to disable write actions like customer creation.
         * If the company has any of the module licenses it'll not be treated as readonly
         */
        isReadonly(): {
            globalReadonly: boolean
            packaging: boolean
            deliveryManagement: boolean
            pod: boolean
            damageReport: boolean
            driverReport: boolean
        } {
            const hasModuleLicense = this.licenseList.some((license) =>
                moduleLicenses.includes(license as ModuleLicenseType),
            )
            const isReadonly = this.licenseList.some((license) =>
                readonlyLicenses.includes(license),
            )

            return {
                globalReadonly: !hasModuleLicense && isReadonly,
                pod: this.licenseList.includes(LicenseType.PODModuleReadonly),
                packaging: this.licenseList.includes(LicenseType.PackagingModuleReadonly),
                deliveryManagement: this.licenseList.includes(
                    LicenseType.DeliveryManagementModuleReadonly,
                ),
                damageReport: this.licenseList.includes(LicenseType.DamageReportModuleReadonly),
                driverReport: this.licenseList.includes(LicenseType.DriverReportModuleReadonly),
            }
        },
        isLoggedIn: (state) => !!state.user,
        isImpersonating: (state) => state.isImpersonatingFilament || state.isImpersonatingNova,
        company: (state) => state.companies.find((company) => company.id === state.companyId)!,
        licenseList: (state) =>
            Object.keys(state.licenses).map((license): LicenseType => parseInt(license, 10)),
        hasCompany() {
            return !!this.company
        },
    },
    actions: {
        switchCompany(companyId: string) {
            localStorage.setItem(currentCompanyKey, companyId)
            window.location.href = '/'
        },
        async fetchLicenses() {
            if (!this.user || !this.companyId) return

            licensePromise = axios.get<licenseObject>(
                window.route('company.licenses', { company: this.companyId }),
            )

            const response = await licensePromise

            this.licenses = response.data
        },
        async licensesLoaded(): Promise<void> {
            if (!licensePromise) return

            await licensePromise
        },
        updateUser(newUser: Partial<WindowUser>) {
            this.user = { ...this.user, ...newUser } as WindowUser
        },
        hasPermission(permission: PermissionType): boolean {
            return this.company?.permissions.includes(permission) ?? false
        },
        /**
         * Checks if the current company has the given license.
         * When multiple licenses are provided it'll return true if the company just has one of them
         * @param license
         */
        hasLicense(license: LicenseType | LicenseType[]) {
            if (Array.isArray(license)) {
                return license.filter((value) => this.licenseList.includes(value)).length > 0
            }

            return this.licenseList.includes(license)
        },
        /**
         * Sets currency for the current company
         */
        setCurrency(currency: string) {
            // Because we have the reference to the company object we can update it like so
            this.company.options.currency = currency
        },
        deliveryManagementPreferences(): DeliveryManagementPreferences | null {
            const preferences = this.licenses[LicenseType.DeliveryManagementModule] as
                | DeliveryManagementPreferences
                | undefined

            return preferences ?? null
        },

        packagingManagementPreferences(): PackagingPreferences | null {
            const preferences = this.licenses[LicenseType.PackagingModule] as
                | PackagingPreferences
                | undefined

            return preferences ?? null
        },
    },
})
